import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import MessageCard from "@/components/MessageCard";
import { useLang } from "@/lib/useLang";
import { getOrCreateVoterId } from "@/lib/voter";
import Analytics from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAGE_SIZE = 20;
const VOTED_KEY = "kerala_speaks_upvoted";

function loadUpvotedSet() {
  try {
    const raw = localStorage.getItem(VOTED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}

const SORT_TABS = [
  { id: "trending", key: "sort_trending" },
  { id: "upvotes", key: "sort_upvotes" },
  { id: "latest", key: "sort_latest" },
];

export const MessageFeed = ({ refreshKey = 0 }) => {
  const { t, lang } = useLang();
  const [voterId] = useState(getOrCreateVoterId);
  const [sort, setSort] = useState("trending");
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [upvotedSet, setUpvotedSet] = useState(() => loadUpvotedSet());
  const sentinelRef = useRef(null);

  // Categories
  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => setCategories(r.data?.categories || []))
      .catch(() => {});
  }, []);

  // Server-side upvoted ids (best-effort merge with local cache)
  useEffect(() => {
    axios.get(`${API}/upvoted-by/${voterId}`).then((r) => {
      const ids = r.data?.message_ids || [];
      if (ids.length) {
        setUpvotedSet((prev) => {
          const next = new Set(prev);
          ids.forEach((id) => next.add(id));
          try {
            localStorage.setItem(VOTED_KEY, JSON.stringify([...next].slice(-2000)));
          } catch { /* ignore */ }
          return next;
        });
      }
    }).catch(() => {});
  }, [voterId]);

  const fetchPage = useCallback(async (nextSkip, replace = false) => {
    setLoading(true);
    try {
      const params = { sort, skip: nextSkip, limit: PAGE_SIZE };
      if (category && category !== "all") params.category = category;
      const r = await axios.get(`${API}/messages`, { params });
      const newMsgs = r.data?.messages || [];
      setTotal(r.data?.total || 0);
      setHasMore(!!r.data?.has_more);
      setMessages((prev) => (replace ? newMsgs : [...prev, ...newMsgs]));
    } catch (e) {
      // soft fail — feed remains empty
    } finally {
      setLoading(false);
    }
  }, [sort, category]);

  // Reset on sort/category/refresh change
  useEffect(() => {
    setSkip(0);
    fetchPage(0, true);
  }, [sort, category, refreshKey, fetchPage]);

  // Live refresh — every 25s, refetch the FIRST page only (so new messages
  // appear at top and upvote counts stay current). Pauses on hidden tab + when
  // user has scrolled into infinite-scroll territory (to avoid jumping).
  useEffect(() => {
    let id = null;
    const refresh = async () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (skip > 0) return; // user is reading older posts; don't reset their view
      try {
        const params = { sort, skip: 0, limit: PAGE_SIZE };
        if (category && category !== "all") params.category = category;
        const r = await axios.get(`${API}/messages`, { params });
        const newMsgs = r.data?.messages || [];
        setTotal(r.data?.total || 0);
        setHasMore(!!r.data?.has_more);
        setMessages((prev) => {
          // Merge: keep upvote counts from server, preserve order of first page.
          // If lengths/ids differ, replace; else patch upvotes in place.
          if (prev.length !== newMsgs.length) return newMsgs;
          const prevIds = prev.map((m) => m.id).join("|");
          const nextIds = newMsgs.map((m) => m.id).join("|");
          if (prevIds !== nextIds) return newMsgs;
          // Same order — just refresh mutable fields
          return prev.map((m, i) => ({ ...m, upvotes: newMsgs[i].upvotes, status: newMsgs[i].status, featured: newMsgs[i].featured }));
        });
      } catch { /* silent */ }
    };
    const start = () => { if (!id) id = setInterval(refresh, 25_000); };
    const stop = () => { if (id) { clearInterval(id); id = null; } };
    const onVis = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "visible") { refresh(); start(); } else { stop(); }
    };
    if (typeof document !== "undefined") {
      if (document.visibilityState === "visible") start();
      document.addEventListener("visibilitychange", onVis);
    }
    return () => { stop(); if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVis); };
  }, [sort, category, skip]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    if (!sentinelRef.current) return;
    const node = sentinelRef.current;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && hasMore && !loading) {
          const nextSkip = skip + PAGE_SIZE;
          setSkip(nextSkip);
          fetchPage(nextSkip, false);
        }
      });
    }, { rootMargin: "300px" });
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, loading, skip, fetchPage]);

  const categoryName = (id) => {
    const c = categories.find((x) => x.id === id);
    if (!c) return id;
    return lang === "ml" && c.name_ml ? c.name_ml : c.name;
  };

  return (
    <section className="space-y-6" data-testid="message-feed">
      {/* Header row: sort tabs + category filter + count */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600">
            {t("feed_eyebrow")}
          </p>
          <h2
            className="font-display uppercase leading-none mt-2"
            style={{ fontSize: "clamp(1.75rem, 4vw, 2.5rem)" }}
            data-testid="feed-heading"
          >
            {t("feed_heading")}
          </h2>
          <p className="text-xs uppercase font-bold tracking-[0.25em] text-neutral-600 mt-1" data-testid="feed-count">
            {total.toLocaleString()} {t("feed_messages_word")}
          </p>
        </div>

        <div
          className="flex border-4 border-black overflow-hidden"
          role="tablist"
          aria-label={t("sort_aria")}
          data-testid="sort-tabs"
        >
          {SORT_TABS.map((tab, i) => {
            const active = sort === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                aria-pressed={active}
                onClick={() => { setSort(tab.id); Analytics.sortChange(tab.id); }}
                data-testid={`sort-tab-${tab.id}`}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm uppercase font-extrabold tracking-[0.2em] transition-colors"
                style={{
                  background: active ? "#E3000F" : "#FDFBF7",
                  color: active ? "#FFFFFF" : "#0A0A0A",
                  borderLeft: i === 0 ? "none" : "2px solid #0A0A0A",
                }}
              >
                {t(tab.key)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Category chip filter — horizontal scroll on mobile */}
      <div className="overflow-x-auto -mx-2 px-2" data-testid="category-filter">
        <div className="flex gap-2 pb-1 min-w-max">
          <CategoryChip
            label={t("category_all")}
            active={category === "all"}
            onClick={() => { setCategory("all"); Analytics.categoryFilter("all"); }}
            testId="category-chip-all"
          />
          {categories.map((c) => (
            <CategoryChip
              key={c.id}
              label={lang === "ml" && c.name_ml ? c.name_ml : c.name}
              active={category === c.id}
              onClick={() => { setCategory(c.id); Analytics.categoryFilter(c.id); }}
              testId={`category-chip-${c.id}`}
            />
          ))}
        </div>
      </div>

      {/* Messages */}
      {messages.length === 0 && !loading ? (
        <div
          className="brutal-card p-6 text-center"
          data-testid="feed-empty"
        >
          <p className="text-sm uppercase font-extrabold tracking-[0.25em] text-neutral-600">
            {t("feed_empty")}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((m) => (
            <MessageCard
              key={m.id}
              msg={m}
              categoryName={categoryName(m.category)}
              initiallyUpvoted={upvotedSet.has(m.id)}
              onUpvoted={(id) => setUpvotedSet((prev) => new Set(prev).add(id))}
            />
          ))}
        </div>
      )}

      {/* Sentinel + loading state */}
      <div ref={sentinelRef} data-testid="feed-sentinel" aria-hidden="true" />
      {loading && (
        <div className="flex items-center justify-center gap-2 py-4 text-neutral-600">
          <Loader2 size={16} strokeWidth={3} className="animate-spin" />
          <span className="text-xs uppercase font-bold tracking-[0.25em]">{t("feed_loading")}</span>
        </div>
      )}
      {!hasMore && messages.length > 0 && (
        <p className="text-xs uppercase font-bold tracking-[0.25em] text-neutral-500 text-center py-4" data-testid="feed-end">
          {t("feed_end")}
        </p>
      )}
    </section>
  );
};

const CategoryChip = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-[11px] uppercase font-extrabold tracking-[0.2em] px-3 py-2 border-2 border-black shrink-0"
    style={{
      background: active ? "#0A0A0A" : "#FDFBF7",
      color: active ? "#FF9933" : "#0A0A0A",
    }}
    aria-pressed={active}
    data-testid={testId}
  >
    {label}
  </button>
);

export default MessageFeed;
